import type { ComponentPropsWithoutRef } from "react";

import clsx from "clsx";
import { memo } from "react";

import { Typo } from "../Typo";
import { filterButtonClassName, filterButtonTextColor } from "./filterButtonStyles";

export type SegmentOption<T extends string = string> = {
  label: string;
  value: T;
  disabled?: boolean;
};

export type SegmentControlSize = "sm" | "md";

export type SegmentControlVariant = "track" | "filterButtons";

export type SegmentControlProps<T extends string = string> = Omit<
  ComponentPropsWithoutRef<"div">,
  "onChange"
> & {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  label?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  size?: SegmentControlSize;
  variant?: SegmentControlVariant;
  scrollable?: boolean;
  "data-testid"?: string;
};

const SegmentControlInner = <T extends string = string>({
  options,
  value,
  onChange,
  label,
  "aria-label": ariaLabel,
  disabled = false,
  fullWidth = false,
  size = "md",
  variant = "track",
  scrollable = false,
  "data-testid": testId = "segment-control",
  className = "",
  ...rest
}: SegmentControlProps<T>) => {
  const isSmall = size === "sm";
  const isFilterButtons = variant === "filterButtons";
  const trackClassName = clsx(
    "scrollbar-minimal-hover inline-flex gap-0.5 overflow-x-auto rounded-[10px] bg-surface-tonal-default p-1",
  );

  const filterButtonsWrapClassName = clsx("flex flex-wrap gap-2");
  const filterButtonsScrollableClassName = clsx(
    "scrollbar-minimal-hover overflow-x-auto",
    "flex inline-flex gap-2",
  );

  const groupClassName = isFilterButtons
    ? scrollable
      ? filterButtonsScrollableClassName
      : filterButtonsWrapClassName
    : trackClassName;

  return (
    <div
      className={clsx("flex flex-col gap-3", fullWidth ? "w-full" : "w-fit")}
    >
      {label && <Typo type="labelSM">{label}</Typo>}
      <div
        className={clsx(groupClassName, disabled && "opacity-50", className)}
        role="radiogroup"
        aria-label={label ?? ariaLabel ?? "segment-control"}
        data-testid={testId}
        {...rest}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          const isDisabled = disabled || option.disabled;

          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isDisabled}
              onClick={() => {
                if (!isDisabled && !isSelected) {
                  onChange(option.value);
                }
              }}
              data-testid={`${testId}-option-${option.value}`}
              className={clsx(
                "whitespace-nowrap rounded-lg",
                isFilterButtons
                  ? "transition-colors duration-200 ease-out"
                  : "transition-[background-color,box-shadow] duration-200 ease-out",
                isSmall
                  ? "typo-label-xs px-2 py-1"
                  : isFilterButtons
                    ? "typo-label-sm px-5 py-2.5"
                    : "typo-label-sm px-4 py-[7px]",
                fullWidth && "flex-1",
                isFilterButtons
                  ? filterButtonClassName(isSelected)
                  : isSelected
                    ? "bg-surface-primary text-text-primary shadow-[0_1px_2px_rgba(3,20,46,0.10)]"
                    : "bg-transparent text-text-secondary shadow-[0_0_0_rgba(3,20,46,0)] hover:text-text-primary",
                isDisabled ? "cursor-not-allowed" : "cursor-pointer",
              )}
            >
              <Typo
                element="span"
                type={isSmall ? "labelXS" : "labelSM"}
                textColor={
                  isFilterButtons
                    ? filterButtonTextColor(isSelected)
                    : isSelected
                      ? "text-text-primary"
                      : "text-text-secondary"
                }
                className={clsx(
                  "transition-colors duration-200 ease-out",
                  !isFilterButtons && !isSelected && "hover:text-text-primary",
                )}
              >
                {option.label}
              </Typo>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const SegmentControl = memo(
  SegmentControlInner,
) as typeof SegmentControlInner;
